// Cohort context for the founder portal at /<slug>/founder/*.
//
// App.tsx wraps the founder route block in <FounderCohortProvider slug={...}>
// after parsing slug from the URL. Inside any founder page, call useCohort()
// to get { slug, theme }. Build founder-portal links via fp(slug, sub).
//
// Why a context and not just a route param: every founder page needs the
// slug for nav links + signout redirect. Threading it through props would
// touch every component. A provider keeps it one read per component.

import { createContext, useContext, type ReactNode } from "react";
import { themeForSlug, type CohortTheme } from "@/config/cohort-themes";

type Ctx = { slug: string; theme: CohortTheme };

const CohortCtx = createContext<Ctx | null>(null);

export function FounderCohortProvider({
  slug,
  children,
}: {
  slug: string;
  children: ReactNode;
}) {
  const value: Ctx = { slug, theme: themeForSlug(slug) };
  return <CohortCtx.Provider value={value}>{children}</CohortCtx.Provider>;
}

export function useCohort(): Ctx {
  const ctx = useContext(CohortCtx);
  if (!ctx) {
    throw new Error("useCohort() must be used inside <FounderCohortProvider>");
  }
  return ctx;
}

// Build a founder portal path. fp helper keeps URL construction in one
// place so a future scheme change (subdomains, white-label hosts) is a
// one-file edit.
//   fp("1to10")                 -> "/1to10/founder"
//   fp("1to10", "thread")       -> "/1to10/founder/thread"
//   fp("1to10", "/onboarding")  -> "/1to10/founder/onboarding"
export function fp(slug: string, sub: string = ""): string {
  if (!sub) return `/${slug}/founder`;
  const cleaned = sub.replace(/^\/+/, "");
  return `/${slug}/founder/${cleaned}`;
}
